/**
 * Google Tag Manager (GTM) utility functions
 * Provides easy-to-use functions for tracking events and page views
 */

// Check if GTM is loaded
export const isGTMLoaded = () => {
  return typeof window !== 'undefined' && window.dataLayer;
};

// Track page view
export const trackPageView = (pageData = {}) => {
  if (!isGTMLoaded()) return;
  
  const defaultData = {
    event: 'page_view',
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_search: window.location.search,
    page_hash: window.location.hash,
    timestamp: new Date().toISOString()
  };
  
  window.dataLayer.push({
    ...defaultData,
    ...pageData
  });
};

// Track custom events
export const trackEvent = (eventName, eventData = {}) => {
  if (!isGTMLoaded()) return;
  
  window.dataLayer.push({
    event: eventName,
    ...eventData,
    timestamp: new Date().toISOString()
  });
};

// Track user interactions
export const trackUserInteraction = (interactionType, elementData = {}) => {
  if (!isGTMLoaded()) return;
  
  window.dataLayer.push({
    event: 'user_interaction',
    interaction_type: interactionType,
    ...elementData,
    timestamp: new Date().toISOString()
  });
};

// Track form submissions
export const trackFormSubmission = (formName, formData = {}) => {
  if (!isGTMLoaded()) return;
  
  window.dataLayer.push({
    event: 'form_submit',
    form_name: formName,
    ...formData,
    timestamp: new Date().toISOString()
  });
};

// Track button clicks
export const trackButtonClick = (buttonName, buttonData = {}) => {
  if (!isGTMLoaded()) return;
  
  window.dataLayer.push({
    event: 'button_click',
    button_name: buttonName,
    ...buttonData,
    timestamp: new Date().toISOString()
  });
};

// Track external link clicks
export const trackExternalLink = (url, linkData = {}) => {
  if (!isGTMLoaded()) return;
  
  window.dataLayer.push({
    event: 'external_link_click',
    external_url: url,
    ...linkData,
    timestamp: new Date().toISOString()
  });
};

// Track scroll depth
export const trackScrollDepth = (depth) => {
  if (!isGTMLoaded()) return;
  
  window.dataLayer.push({
    event: 'scroll_depth',
    scroll_depth: depth,
    timestamp: new Date().toISOString()
  });
};

// Track time on page
export const trackTimeOnPage = (timeInSeconds) => {
  if (!isGTMLoaded()) return;
  
  window.dataLayer.push({
    event: 'time_on_page',
    time_seconds: timeInSeconds,
    timestamp: new Date().toISOString()
  });
};

// Initialize GTM tracking for the app
export const initializeGTMTracking = () => {
  if (typeof window === 'undefined') return;
  
  // Set up scroll depth tracking
  let maxScrollDepth = 0;
  let scrollTrackingInterval;
  
  const trackScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);
    
    if (scrollPercent > maxScrollDepth) {
      maxScrollDepth = scrollPercent;
      
      // Track at 25%, 50%, 75%, and 100% scroll points
      if ([25, 50, 75, 100].includes(maxScrollDepth)) {
        trackScrollDepth(maxScrollDepth);
      }
    }
  };
  
  // Start scroll tracking
  scrollTrackingInterval = setInterval(trackScroll, 1000);
  
  // Cleanup function
  return () => {
    if (scrollTrackingInterval) {
      clearInterval(scrollTrackingInterval);
    }
  };
}; 